import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';

/**
 * Report UI State Interface
 */
export interface ReportUIState {
  // Loading states
  isLoadingData: boolean;
  isLoadingCharts: {
    [chartId: string]: boolean;
  };

  // Error states
  error: {
    message: string;
    code: string;
    timestamp: number;
  } | null;

  // Pagination
  currentPage: Record<string, number>; // Per table
  pageSize: Record<string, number>; // Per table

  // Visibility toggles
  visibleOutlets: Record<string, boolean>; // Which outlets shown in charts
  visibleCharts: Record<string, boolean>; // Which charts visible

  // Sorting
  sortBy: Record<string, string>; // Per table
  sortOrder: Record<string, 'asc' | 'desc'>; // Per table

  // UI preferences
  expandedSections: Record<string, boolean>; // Which report sections expanded
  compactMode: boolean;

  // Export state
  isExporting: boolean;
  exportProgress: number;
}

// Storage key
const STORAGE_KEY = 'cashierpos_report_ui_preferences';

/**
 * Load UI preferences from localStorage
 */
const loadUIPreferencesFromStorage = (): Partial<ReportUIState> | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored);

    // Version check
    if (parsed.version !== 1) return null;

    return parsed.data;
  } catch (error) {
    console.error('Error loading UI preferences from localStorage:', error);
    return null;
  }
};

/**
 * Save UI preferences to localStorage
 */
const saveUIPreferencesToStorage = (state: ReportUIState): void => {
  try {
    // Only save preferences, not transient states
    const toSave = {
      version: 1,
      savedAt: new Date().toISOString(),
      data: {
        compactMode: state.compactMode,
        expandedSections: state.expandedSections,
        pageSize: state.pageSize,
        visibleCharts: state.visibleCharts,
      },
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch (error) {
    console.error('Error saving UI preferences to localStorage:', error);
  }
};

/**
 * Initial state
 */
const getInitialState = (): ReportUIState => {
  const savedPreferences = loadUIPreferencesFromStorage();

  return {
    isLoadingData: false,
    isLoadingCharts: {},
    error: null,
    currentPage: {},
    pageSize: savedPreferences?.pageSize || {},
    visibleOutlets: {},
    visibleCharts: savedPreferences?.visibleCharts || {},
    sortBy: {},
    sortOrder: {},
    expandedSections: savedPreferences?.expandedSections || {},
    compactMode: savedPreferences?.compactMode || false,
    isExporting: false,
    exportProgress: 0,
  };
};

/**
 * Report UI Slice
 */
export const reportUISlice = createSlice({
  name: 'reportUI',
  initialState: getInitialState(),
  reducers: {
    // Loading states
    setLoadingData: (state, action: PayloadAction<boolean>) => {
      state.isLoadingData = action.payload;
    },

    setLoadingChart: (state, action: PayloadAction<{ chartId: string; isLoading: boolean }>) => {
      state.isLoadingCharts[action.payload.chartId] = action.payload.isLoading;
    },

    // Error handling
    setError: (state, action: PayloadAction<{ message: string; code: string }>) => {
      state.error = {
        message: action.payload.message,
        code: action.payload.code,
        timestamp: Date.now(),
      };
    },

    clearError: (state) => {
      state.error = null;
    },

    // Pagination
    setCurrentPage: (state, action: PayloadAction<{ tableId: string; page: number }>) => {
      state.currentPage[action.payload.tableId] = action.payload.page;
    },

    setPageSize: (state, action: PayloadAction<{ tableId: string; size: number }>) => {
      state.pageSize[action.payload.tableId] = action.payload.size;
      saveUIPreferencesToStorage(state);
    },

    // Visibility toggles
    toggleOutletVisibility: (state, action: PayloadAction<string>) => {
      const outletId = action.payload;
      state.visibleOutlets[outletId] = !state.visibleOutlets[outletId];
    },

    setOutletVisibility: (state, action: PayloadAction<{ outletId: string; visible: boolean }>) => {
      state.visibleOutlets[action.payload.outletId] = action.payload.visible;
    },

    toggleChartVisibility: (state, action: PayloadAction<string>) => {
      const chartId = action.payload;
      state.visibleCharts[chartId] = !state.visibleCharts[chartId];
      saveUIPreferencesToStorage(state);
    },

    setChartVisibility: (state, action: PayloadAction<{ chartId: string; visible: boolean }>) => {
      state.visibleCharts[action.payload.chartId] = action.payload.visible;
      saveUIPreferencesToStorage(state);
    },

    // Sorting
    setSorting: (state, action: PayloadAction<{ tableId: string; column: string; order: 'asc' | 'desc' }>) => {
      state.sortBy[action.payload.tableId] = action.payload.column;
      state.sortOrder[action.payload.tableId] = action.payload.order;
    },

    // Section expansion
    toggleSection: (state, action: PayloadAction<string>) => {
      const sectionId = action.payload;
      state.expandedSections[sectionId] = !state.expandedSections[sectionId];
      saveUIPreferencesToStorage(state);
    },

    setSection: (state, action: PayloadAction<{ sectionId: string; expanded: boolean }>) => {
      state.expandedSections[action.payload.sectionId] = action.payload.expanded;
      saveUIPreferencesToStorage(state);
    },

    // Compact mode
    setCompactMode: (state, action: PayloadAction<boolean>) => {
      state.compactMode = action.payload;
      saveUIPreferencesToStorage(state);
    },

    // Export state
    setExporting: (state, action: PayloadAction<{ isExporting: boolean; progress?: number }>) => {
      state.isExporting = action.payload.isExporting;
      state.exportProgress = action.payload.progress || 0;
    },

    // Reset all UI state (except preferences)
    resetUIState: (state) => {
      state.isLoadingData = false;
      state.isLoadingCharts = {};
      state.error = null;
      state.currentPage = {};
      state.visibleOutlets = {};
      state.sortBy = {};
      state.sortOrder = {};
      state.isExporting = false;
      state.exportProgress = 0;
    },

    // Load preferences from localStorage
    loadUIPreferences: (state) => {
      const saved = loadUIPreferencesFromStorage();
      if (saved) {
        if (saved.compactMode !== undefined) state.compactMode = saved.compactMode;
        if (saved.expandedSections) state.expandedSections = saved.expandedSections;
        if (saved.pageSize) state.pageSize = saved.pageSize;
        if (saved.visibleCharts) state.visibleCharts = saved.visibleCharts;
      }
    },

    // Clear localStorage
    clearPersistedUIPreferences: () => {
      localStorage.removeItem(STORAGE_KEY);
    },
  },
});

// Actions
export const {
  setLoadingData,
  setLoadingChart,
  setError,
  clearError,
  setCurrentPage,
  setPageSize,
  toggleOutletVisibility,
  setOutletVisibility,
  toggleChartVisibility,
  setChartVisibility,
  setSorting,
  toggleSection,
  setSection,
  setCompactMode,
  setExporting,
  resetUIState,
  loadUIPreferences,
  clearPersistedUIPreferences,
} = reportUISlice.actions;

// Selectors
export const selectReportUI = (state: RootState) => state.reportUI;
export const selectIsLoading = (state: RootState) => state.reportUI.isLoadingData || Object.values(state.reportUI.isLoadingCharts).some(loading => loading);
export const selectError = (state: RootState) => state.reportUI.error;
export const selectIsChartLoading = (chartId: string) => (state: RootState) => state.reportUI.isLoadingCharts[chartId] || false;
export const selectVisibleOutlets = (state: RootState) => state.reportUI.visibleOutlets;
export const selectTableSort = (tableId: string) => (state: RootState) => ({
  sortBy: state.reportUI.sortBy[tableId],
  sortOrder: state.reportUI.sortOrder[tableId],
});
export const selectTablePagination = (tableId: string) => (state: RootState) => ({
  currentPage: state.reportUI.currentPage[tableId] || 0,
  pageSize: state.reportUI.pageSize[tableId] || 10,
});
export const selectIsSectionExpanded = (sectionId: string) => (state: RootState) => state.reportUI.expandedSections[sectionId] !== false; // Default to expanded
export const selectIsChartVisible = (chartId: string) => (state: RootState) => state.reportUI.visibleCharts[chartId] !== false; // Default to visible

export default reportUISlice.reducer;
